import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RbacManager,
  RoleAssignmentDialog,
  RoleChips as DesignRoleChips,
  RoleMultiSelect,
  type RbacMember,
  type RbacPermission,
  type RbacRole,
  type RbacRoleDraft,
  type RbacView,
} from "@studio-features/meta-crew/rbac";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { useAuth } from "../lib/auth";
import type { Me, PermissionInfo, RoleRef, RoleView } from "../lib/types";
import { Loading, PanelError } from "../console/ui";

/**
 * 역할 (RBAC, docs/meta-crew-role-permission-design.md v2) — 시안 05be2fc(요청서 24절) RbacManager·RoleAssignmentDialog 에 실제 API 를 잇는 어댑터.
 * - 관리 콘솔 "역할과 권한": 관리 콘솔 역할 + 고객사 공용 역할 (/admin/roles)
 * - 고객사 상세 › 권한 / 고객 웹 조직 › 역할: 공용 역할(읽기) + 그 고객사 전용 역할 (/c/:id/roles)
 * 권한 검사(범위·권한 올리기·잠김·마지막 관리자·정지)는 서버가 최종으로 한다. 여기서는 화면 안내용 grantable 만 계산한다.
 */

type RoleList = { permissions: PermissionInfo[]; roles: RoleView[] };

const globalKey = ["admin", "roles"] as const;
const tenantKey = (customerId: string) => ["c", customerId, "roles"] as const;

export const useGlobalRoles = (enabled = true) => useQuery({ queryKey: globalKey, queryFn: () => api.get<RoleList>("/admin/roles"), enabled });
export const useTenantRoles = (customerId: string, enabled = true) =>
  useQuery({ queryKey: tenantKey(customerId), queryFn: () => api.get<RoleList>(`/c/${customerId}/roles`), enabled: enabled && !!customerId });

/* ---------------- 서버 응답 → 시안 view ---------------- */

const toRole = (r: RoleView): RbacRole => ({
  id: r.id,
  scope: r.scope,
  customerId: r.customerId,
  shared: r.shared,
  code: r.code ?? undefined,
  name: r.name,
  description: r.description ?? "",
  permissions: r.permissions,
  builtin: r.builtin,
  locked: r.locked,
  memberCount: r.memberCount,
});

/** 권한 코드의 읽기/쓰기 구분 (화면 표시용). */
const accessOf = (code: string): "read" | "write" => (/(_READ|_READ_ALL)$/.test(code) ? "read" : "write");

/** 줄 수 있는 권한: 내가 가진 권한 (관리자는 고객 전용 권한도 고객사 역할에 넣을 수 있다 — 서버 규칙과 같음). */
function toPermission(p: PermissionInfo, me: Me | null): RbacPermission {
  const mine = !!me?.permissions.includes(p.code);
  const grantable = p.locked || mine || (!!me?.isStaff && p.audience === "customer");
  return {
    code: p.code,
    group: p.group,
    label: p.label,
    description: p.description,
    audience: p.audience === "staff" || p.audience === "customer" ? p.audience : "both",
    locked: p.locked,
    scopes: p.scopes,
    access: accessOf(p.code),
    grantable,
    disabledReason: grantable ? undefined : "내게 없는 권한이라 다른 역할에 줄 수 없습니다.",
  };
}

function toView(list: RoleList, me: Me | null, o: { scope: "staff" | "customer"; customerId: string | null; customerName?: string; canManage: boolean; readOnlyReason?: string }): RbacView {
  return {
    scope: o.scope,
    customerId: o.customerId,
    customerName: o.customerName,
    roles: list.roles.map(toRole),
    permissions: list.permissions.map((p) => toPermission(p, me)),
    canManage: o.canManage,
    readOnlyReason: o.readOnlyReason,
  };
}

const body = (d: RbacRoleDraft) => ({ name: d.name, description: d.description || null, permissions: d.permissions });

/** 저장·삭제·회원 읽기를 경로 하나로 묶는다. 저장 뒤 목록을 다시 읽어 시안에 돌려준다. */
function useRbacActions(o: { listPath: string; key: readonly unknown[]; build: (list: RoleList) => RbacView; createExtra?: (d: RbacRoleDraft) => Record<string, unknown> }) {
  const qc = useQueryClient();
  const reload = async (): Promise<RbacView> => {
    await qc.invalidateQueries({ queryKey: [...o.key] });
    // 역할이 바뀌면 회원 목록의 역할 칩·내 권한도 다시 읽는다
    await qc.invalidateQueries({ queryKey: ["admin", "roles"] });
    return o.build(await qc.fetchQuery({ queryKey: [...o.key], queryFn: () => api.get<RoleList>(o.listPath) }));
  };
  return {
    // 복제(sourceRoleId)는 권한을 복사한 새 역할 만들기 — 서버에는 POST 하나뿐
    onSave: async (d: RbacRoleDraft) => {
      if (d.id) await api.patch(`${o.listPath}/${d.id}`, body(d));
      else await api.post(o.listPath, { ...body(d), ...(o.createExtra?.(d) ?? {}) });
    },
    // 시안이 묶인 회원 목록을 보여 주고 다시 확인한 뒤에만 부른다 → force=1
    onDelete: async (id: string) => {
      await api.del(`${o.listPath}/${id}?force=1`);
    },
    onLoadMembers: async (id: string): Promise<RbacMember[]> => {
      const rows = (await api.page<{ id: string; name: string; email: string; jobTitle: string | null }>(`${o.listPath}/${id}/members`)).items;
      return rows.map((m) => ({ id: m.id, name: m.name, email: m.email, jobTitle: m.jobTitle ?? undefined }));
    },
    onReload: reload,
  };
}

/* ---------------- 회원 역할 표시·지정 (관리자 목록·고객사 회원) ---------------- */

/** 역할 이름 칩. */
export function RoleChips({ roles }: { roles: RoleRef[] }) {
  return <DesignRoleChips roles={roles} />;
}

/** 고를 역할 목록의 범위 (관리 콘솔 / 고객사). 고객사 전용 역할이 있으면 그 고객사 id. */
function scopeOf(options: RoleView[]): { scope: "staff" | "customer"; customerId: string | null } {
  if (options.some((r) => r.scope === "staff")) return { scope: "staff", customerId: null };
  return { scope: "customer", customerId: options.find((r) => r.customerId)?.customerId ?? null };
}

/** 역할 여러 개 고르기 (회원 추가 폼). 시안 RoleMultiSelect. */
export function RoleChecklist({ options, value, onChange }: { options: RoleView[]; value: string[]; onChange: (ids: string[]) => void }) {
  const s = scopeOf(options);
  return <RoleMultiSelect availableRoles={options.map(toRole)} selectedIds={value} onChange={onChange} scope={s.scope} customerId={s.customerId} />;
}

/** 회원 역할 지정 다이얼로그 (전체 교체). 시안 RoleAssignmentDialog. */
export function AssignRolesDialog({
  open,
  onClose,
  memberName,
  options,
  current,
  path,
  invalidate,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  memberName: string;
  options: RoleView[];
  current: RoleRef[];
  /** PUT 경로 (/admin/users/:id/roles 또는 /c/:customerId/members/:id/roles) */
  path: string;
  invalidate: readonly unknown[][];
  onDone?: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const me = useAuth((x) => x.me);
  const s = scopeOf(options);
  const canManage = s.scope === "staff" ? can(me, "USER_MANAGE") || can(me, "MEMBER_MANAGE") : can(me, "MEMBER_MANAGE");
  return (
    <RoleAssignmentDialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      memberName={memberName}
      selectedIds={current.map((r) => r.id)}
      availableRoles={options.map(toRole)}
      scope={s.scope}
      customerId={s.customerId}
      canManage={canManage}
      onSave={async (ids) => {
        // 실패하면 시안이 서버 문구를 그대로 보여 주고 고른 값을 남긴다
        await api.put(path, { roleIds: ids });
        await Promise.all([...invalidate, [...globalKey]].map((k) => qc.invalidateQueries({ queryKey: k })));
        onDone?.("역할을 바꿨습니다.");
        onClose();
      }}
    />
  );
}

/* ---------------- 역할 관리 화면 ---------------- */

/** 관리 콘솔 › 역할과 권한: 관리 콘솔 역할 + 고객사 공용 역할. */
export function RolesScreen({ me }: { me: Me }) {
  const q = useGlobalRoles();
  const manage = can(me, "ROLE_MANAGE");
  const readOnlyReason = manage ? undefined : "역할 관리 권한이 없어 읽기만 할 수 있습니다.";
  const staffView = (l: RoleList) => toView(l, me, { scope: "staff", customerId: null, canManage: manage, readOnlyReason });
  const sharedView = (l: RoleList) => toView(l, me, { scope: "customer", customerId: null, customerName: "모든 고객사", canManage: manage, readOnlyReason });
  const staffActs = useRbacActions({ listPath: "/admin/roles", key: globalKey, build: staffView, createExtra: () => ({ scope: "staff" }) });
  const sharedActs = useRbacActions({ listPath: "/admin/roles", key: globalKey, build: sharedView, createExtra: () => ({ scope: "customer" }) });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <PanelError error={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  return (
    <>
      <RbacManager scope="staff" customerId={null} view={staffView(q.data)} {...(manage ? staffActs : { onLoadMembers: staffActs.onLoadMembers, onReload: staffActs.onReload })} />
      <RbacManager scope="customer" customerId={null} view={sharedView(q.data)} {...(manage ? sharedActs : { onLoadMembers: sharedActs.onLoadMembers, onReload: sharedActs.onReload })} />
    </>
  );
}

/** 고객사 역할: 공용 역할(관리 콘솔에서만 고침) + 이 고객사 전용 역할 (소유자·관리자가 만든다). */
export function CustomerRolesPanel({
  me,
  customerId,
  customerName,
  suspended,
  onDirtyChange,
}: {
  me: Me;
  customerId: string;
  customerName: string;
  suspended?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const q = useTenantRoles(customerId);
  const manage = can(me, "ROLE_MANAGE") && !suspended;
  const readOnlyReason = suspended ? "고객사가 정지 중이라 역할을 바꿀 수 없습니다." : manage ? undefined : "역할 관리 권한이 없어 읽기만 할 수 있습니다.";
  const build = (l: RoleList) => toView(l, me, { scope: "customer", customerId, customerName, canManage: manage, readOnlyReason });
  const acts = useRbacActions({ listPath: `/c/${customerId}/roles`, key: tenantKey(customerId), build });
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <PanelError error={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  return (
    <RbacManager
      scope="customer"
      customerId={customerId}
      customerName={customerName}
      embedded={!me.customerId}
      view={build(q.data)}
      onDirtyChange={onDirtyChange}
      {...(manage ? acts : { onLoadMembers: acts.onLoadMembers, onReload: acts.onReload })}
    />
  );
}
